/**
 * Capture a partner code that arrives straight at this app.
 *
 * yoursmartstack.com does the same job for its own pages, and decorates every
 * cross-link into this app with `?ref=`, so most attributed visitors arrive
 * here already carrying the cookie. This covers the case that misses: an
 * partner who links directly to aitools.yoursmartstack.com — a guide, a tool
 * page, the quiz — which is a perfectly reasonable thing for them to do and
 * would otherwise track nobody at all.
 *
 * The cookie is deliberately identical to the one the homepage writes: same
 * name, same domain, same window. It is read back server-side at signup by
 * server/core/partner.ts, which is the file to keep this in step with. Three
 * copies of one contract across two repositories is not ideal, and the
 * alternative — a shared package for eight lines of cookie handling between a
 * static site with no build step and a Vite app — is worse.
 *
 * Runs for its side effect and returns nothing. Never throws: Safari in
 * private mode and a visitor with cookies disabled both land in the catch, and
 * neither is a reason for the app to fail to start.
 */

/** Must match PARTNER_COOKIE in server/core/partner.ts. */
const COOKIE = "yss_partner";
/** Must match `PARTNER.COOKIE_DOMAIN` in the homepage repo's config.js. */
const DOMAIN = ".yoursmartstack.com";
const PARAM = "ref";
const WINDOW_DAYS = 90;

/** Must match CODE_PATTERN in server/core/partner.ts. */
const CODE = /^[a-z0-9_-]{2,32}$/;

export function capturePartnerRef(): void {
  try {
    const raw = new URLSearchParams(window.location.search).get(PARAM);
    if (!raw) return;

    const code = raw.trim().toLowerCase();
    if (!CODE.test(code)) return;

    const bits = [
      `${COOKIE}=${encodeURIComponent(code)}`,
      "path=/",
      `max-age=${WINDOW_DAYS * 86400}`,
      "samesite=lax",
    ];

    /**
     * Only on real hosts. `Domain=.yoursmartstack.com` is rejected outright on
     * localhost, and `Secure` stops the cookie being set over plain http, so
     * both are skipped in development — otherwise this is untestable anywhere
     * but production, which for an attribution cookie means untested.
     */
    if (window.location.protocol === "https:") {
      bits.push(`domain=${DOMAIN}`, "secure");
    }

    document.cookie = bits.join("; ");
  } catch {
    // No attribution. Never a broken app.
  }
}
